import { useLoad } from "@allergy123/api-client/react";
import { useAuth } from "@allergy123/auth-client/react";
import { Button, Card, Progress, Status } from "@allergy123/ui";
import { Link } from "react-router";
import { classPath, enrollmentFor, formatDate, isComplete, modulePath, nextAvailableClass, sectionOf } from "../lib";

/** Learner home: what is assigned, where they left off, and each module's progress. */
export function HomePage() {
  const { session } = useAuth();
  const data = useLoad(async (api) => {
    const [cat, learning] = await Promise.all([api.catalog(), api.learning.me()]);
    return { cat, learning };
  });
  if (data.state === "error") return <Status kind="error" label={`Couldn't load your learning (${data.error?.message})`} />;
  if (!data.data) return <p className="text-ink-secondary">Loading…</p>;
  const { cat, learning } = data.data;
  const enrollments = learning.enrollments.filter((e) => e.status !== "withdrawn");

  const next = nextAvailableClass(cat, enrollments);

  const sectionsShown = [...new Set(enrollments.map((e) => cat.modules.find((m) => m.code === e.moduleCode)?.sectionId).filter(Boolean))] as string[];

  return (
    <>
      <h1 className="mb-2 text-h1 font-semibold text-anchor">My learning</h1>
      <p className="mb-8 max-w-[68ch] text-ink-secondary">Welcome{session?.claims.name ? `, ${session.claims.name.split(" ")[0]}` : ""}. Your pathway is listed by section; each class is about ten to fifteen minutes, and progress saves as you go.</p>

      {enrollments.length === 0 ? (
        <Card>
          <h2 className="mb-2 text-h3 font-semibold text-anchor">Nothing assigned yet</h2>
          <p className="mb-6 max-w-[68ch]">When your practice assigns training, the Foundation class and your role pathway show up here. You can also browse the catalog and start the Foundation yourself.</p>
          <Button href="/training">Browse the training catalog</Button>
        </Card>
      ) : (
        <>
          {next && (
            <Card tone="tinted" className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-small font-medium text-ink-secondary">{next.progress ? "Continue where you left off" : "Start here"}</p>
                <h2 className="text-h3 font-semibold text-anchor">
                  {next.cls.code} · {next.cls.title}
                </h2>
                <p className="text-small text-ink-secondary">
                  {next.module.title}
                  {next.progress && ` · last opened ${formatDate(next.progress.lastSeenAt)}`}
                </p>
              </div>
              <Button href={classPath(next.cls.code)}>{next.progress ? "Continue" : "Start class"}</Button>
            </Card>
          )}
          {!next && <p className="mb-8 text-ink-secondary">No new classes are available to start. You can review completed classes below; remaining videos will appear when published.</p>}

          {learning.assignments.length > 0 && (
            <p className="mb-8 text-small text-ink-secondary">
              Assigned pathway{learning.assignments.length > 1 ? "s" : ""}: {learning.assignments.map((a) => cat.pathways.find((p) => p.id === a.pathwayId)?.title ?? a.pathwayId).join(", ")} — completion is recommended for your role;
              it becomes required only for a recognition you elect.
            </p>
          )}

          {sectionsShown.map((sectionId) => {
            const section = sectionOf(cat, sectionId);
            const modules = cat.modules.filter((m) => m.sectionId === sectionId && enrollmentFor(enrollments, m.code));
            return (
              <section key={sectionId} className="mb-10">
                <h2 className="mb-4 text-h2 font-semibold text-anchor">{section?.title ?? sectionId}</h2>
                <ul className="grid gap-4 md:grid-cols-2">
                  {modules.map((m) => {
                    const e = enrollmentFor(enrollments, m.code)!;
                    const done = m.classes.filter((k) => isComplete(e, k.code)).length;
                    return (
                      <Card key={m.code} as="li">
                        <p className="text-small font-medium text-ink-secondary">{m.code}</p>
                        <h3 className="mb-3 text-h3 font-semibold text-anchor">
                          <Link to={modulePath(cat, m.code)}>{m.title}</Link>
                        </h3>
                        <Progress value={done} max={m.classes.length} label="Classes complete" />
                        {m.classes.length > 0 && m.classes.every((k) => !k.media.length) && <p className="mt-3 text-small text-ink-secondary">Media for this module is in production.</p>}
                      </Card>
                    );
                  })}
                </ul>
              </section>
            );
          })}
        </>
      )}
    </>
  );
}
